import {
  Array,
  Boolean,
  Literal,
  Null,
  Number,
  Optional,
  Partial,
  Record as RRecord,
  Static,
  String,
  Union,
  Unknown,
} from "runtypes";

export const TableStoreReference = RRecord({
  contentHash: String,
  versionId: String,
  persistence: Optional(Union(Literal("s3"), Literal("local"))),
});
export type TableStoreReference = Static<typeof TableStoreReference>;

export function isPersistedTableRef(ref: TableStoreReference): boolean {
  return ref.persistence === "s3" || ref.persistence == null;
}

export const SqlCacheMetadata = RRecord({
  timestamp: String,
  source: Optional(String),
  cacheHit: Optional(Boolean),
  key: Optional(String),
  tableRef: Optional(TableStoreReference),
  queryExecutionTime: Optional(Number),
  queryStreamTime: Optional(Number),
  cacheReadTime: Optional(Number),
  cacheWriteTime: Optional(Number),
});
export type SqlCacheMetadata = Static<typeof SqlCacheMetadata>;

export const SqlStatus = RRecord({
  finished: Boolean,
  loadedRows: Number,
  loadedBytes: Number.Or(Null),
}).And(
  Partial({
    currentBatchRows: Number,
    message: String.Or(Null),
    numColumns: Number,
    elapsedTime: Number,
    metadata: SqlCacheMetadata.Or(Null),
  }),
);
export type SqlStatus = Static<typeof SqlStatus>;

const HqlNextOutput = RRecord({
  query: String,
  datasets: String,
  /**
   * This is used to distinguish between different queries generated
   */
  name: Union(
    Literal("explore_chart"),
    Literal("explore_table"),
    Literal("explore_pivot"),
    Literal("explore_table_aggregation"),
    Literal("explore_table_page"),
  ),
  temporaryParamMapping: String,
});
export type HqlNextOutput = Static<typeof HqlNextOutput>;

export const SqlCompiledQuery = RRecord({
  // may be truncated if overly long
  query: String,
  // the length of the query if it weren't truncated
  queryLength: Number,
  // may be truncated if overly long
  parameters: Array(Unknown),
  // the number of parameters if they weren't truncated
  parametersLength: Number,
  hqlInfo: Optional(HqlNextOutput),
});
export type SqlCompiledQuery = Static<typeof SqlCompiledQuery>;

export const WritebackStatus = RRecord({
  message: String.Or(Null),
  writtenRows: Number,
  totalRows: Number,
  elapsedTime: Number,
  writtenBytes: Number,
  totalBytes: Number,
});

export type WritebackStatus = Static<typeof WritebackStatus>;

export const MetricCellOutput = RRecord({
  comparisonLabel: String,
  comparisonValue: Union(String, Number),
  title: String,
  value: Union(String, Number),
  comparisonRowCount: Number,
  valueRowCount: Number,
});

export type MetricCellOutput = Static<typeof MetricCellOutput>;
